import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Users,
  Search,
  Filter,
  Eye,
  Mail,
  Phone,
  MapPin,
  Building2,
  RefreshCw,
  ChevronDown,
  X,
  AlertCircle,
  CheckCircle2,
  User,
  Briefcase,
  CreditCard,
  ArrowLeft,
  MoreVertical,
  CheckCircle,
  XCircle,
  Timer,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import Loader from "../../../../components/misc/Loader";

const AttendeeActionsMenu = ({
  onView,
  onApprove,
  onMarkAsPaid,
  onViewRejectionReason,
  isProcessing,
  paymentStatus,
  eventStatus,
  align = "right",
  hasRejectionReason = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
    placement: "bottom",
    maxHeight: 200,
  });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Calculate which actions to show based on event status and payment status
  const normalizedStatus = (eventStatus || "pending").toLowerCase();
  const normalizedPaymentStatus = (paymentStatus || "pending").toLowerCase();
  const isPaid = 
    normalizedPaymentStatus === "paid" ||
    normalizedPaymentStatus === "completed" ||
    normalizedPaymentStatus === "verified";
  
  // Hide approve if already paid
  const showApprove =
    !isPaid && (normalizedStatus === "pending" || normalizedStatus === "rejected");
  const showMarkAsPaid = normalizedStatus === "approved" && !isPaid;

  // Calculate item count dynamically based on what's shown
  let itemCount = 1;
  if (hasRejectionReason) itemCount += 1;
  if (showApprove) itemCount += 1;
  if (showMarkAsPaid) itemCount += 1;

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const itemHeight = 40;
        const dividerHeight = 1;
        const dividers = Math.max(0, itemCount - 1);
        const padding = 0;
        const dropdownHeight =
          itemCount * itemHeight + dividers * dividerHeight + padding;

        const dropdownWidth = 192;
        const spacing = 2;

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        const showAbove =
          spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        let top;
        let maxHeight = dropdownHeight;

        if (showAbove) {
          maxHeight = Math.min(dropdownHeight, spaceAbove - spacing);
          maxHeight = Math.max(maxHeight, 200);
          top = rect.top - dropdownHeight - spacing;
          if (top < 0) {
            top = spacing;
            maxHeight = Math.max(200, rect.top - spacing * 2);
          }
        } else {
          top = rect.bottom + spacing;
          const availableSpace = window.innerHeight - rect.bottom - spacing;
          maxHeight = Math.min(dropdownHeight, availableSpace);
          maxHeight = Math.max(maxHeight, 200);
          if (top + maxHeight > window.innerHeight) {
            maxHeight = Math.max(200, window.innerHeight - top - spacing);
          }
        }

        let left = null;
        let right = null;

        if (align === "right") {
          const buttonRight = rect.right;
          const buttonLeft = rect.left;
          const viewportRight = window.innerWidth;

          const wouldOverflowRight =
            buttonRight + dropdownWidth > viewportRight;
          const rightAlignmentMargin = viewportRight - buttonRight;

          const wouldFitLeft = buttonLeft + dropdownWidth <= viewportRight;
          if (
            wouldOverflowRight ||
            rightAlignmentMargin < 50 ||
            !wouldFitLeft
          ) {
            if (wouldFitLeft) {
              left = buttonLeft;
              right = null;
            } else {
              right = 10;
              left = null;
            }
          } else {
            right = viewportRight - buttonRight + 5;
            left = null;
            if (right < 0) {
              right = 0;
            }
          }
        } else {
          if (rect.left + dropdownWidth > window.innerWidth) {
            right = window.innerWidth - rect.right;
            left = null;
          } else {
            left = rect.left;
            right = null;
          }
        }

        setPosition({
          top,
          left,
          right,
          placement: showAbove ? "top" : "bottom",
          maxHeight: maxHeight,
        });
      }
    };

    if (isOpen) {
      setTimeout(updatePosition, 0);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, align, itemCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedMenu =
        menuRef.current && menuRef.current.contains(event.target);
      const clickedButton =
        buttonRef.current && buttonRef.current.contains(event.target);

      if (!clickedMenu && !clickedButton) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleAction = (action, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsOpen(false);
    if (action && typeof action === "function") {
      requestAnimationFrame(() => {
        action();
      });
    }
  };

  const menuItems = [
    {
      label: "View Details",
      icon: Eye,
      onClick: (e) => handleAction(onView, e),
      divider: true,
      handler: onView,
      color: "text-gray-900",
    },
    ...(hasRejectionReason && onViewRejectionReason
      ? [
          {
            label: "Rejection Reason",
            icon: FileText,
            onClick: (e) => handleAction(onViewRejectionReason, e),
            divider: true,
            handler: onViewRejectionReason,
            color: "text-orange-700",
            disabled: isProcessing,
          },
        ]
      : []),
    ...(showApprove && onApprove
      ? [
          {
            label: "Approve",
            icon: CheckCircle,
            onClick: (e) => handleAction(onApprove, e),
            divider: true,
            handler: onApprove,
            color: "text-blue-700",
            disabled: isProcessing,
          },
        ]
      : []),
    ...(showMarkAsPaid && onMarkAsPaid
      ? [
          {
            label: "Mark as Paid",
            icon: CreditCard,
            onClick: (e) => handleAction(onMarkAsPaid, e),
            divider: false,
            handler: onMarkAsPaid,
            color: "text-green-700",
            disabled: isProcessing || paymentStatus === "paid",
          },
        ]
      : []),
  ].filter((item) => item.handler !== undefined);

  const dropdownContent = isOpen && (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-48 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden"
      style={{
        top: `${position.top}px`,
        ...(position.right !== null && position.right !== undefined
          ? { right: `${position.right}px`, left: "auto" }
          : { left: `${position.left}px`, right: "auto" }),
        maxHeight: `${position.maxHeight}px`,
      }}
    >
      {menuItems.map((item, index) => {
        const isLastItem = index === menuItems.length - 1;
        return (
          <React.Fragment key={index}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.onClick && !item.disabled) {
                  item.onClick(e);
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              disabled={item.disabled}
              className={`flex items-center w-full px-4 py-2.5 text-sm text-left transition-colors ${
                item.disabled
                  ? "opacity-50 cursor-not-allowed"
                  : `hover:bg-gray-50 ${item.color}`
              }`}
              type="button"
            >
              <item.icon className="flex-shrink-0 mr-3 w-4 h-4" />
              {item.label}
            </button>
            {item.divider && !isLastItem && (
              <div className="h-px bg-gray-200" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="inline-block relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 text-gray-400 rounded-lg transition-colors hover:text-orange-600 hover:bg-orange-50"
          aria-label="Attendee actions"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </div>
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </>
  );
};

const TradeMissionAttendees = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [showRejectionReasonModal, setShowRejectionReasonModal] =
    useState(false);
  const [attendeeToAction, setAttendeeToAction] = useState(null);

  // Fetch events
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch attendees when event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees(selectedEvent.slug || selectedEvent.event_slug);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getEvents();
      if (response?.status && response?.data) {
        let eventsData = [];
        if (response.data.status_code === 200 && response.data.data) {
          eventsData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }

        // Filter for only trade missions
        const filteredEvents = eventsData.filter((event) => {
          if (!event.event_type || !Array.isArray(event.event_type)) {
            return false;
          }
          return event.event_type.includes("trade_mission");
        });

        setEvents(filteredEvents);

        // Check if there's a pre-selected event from navigation state
        const preSelectedEvent = location.state?.event;
        if (
          preSelectedEvent &&
          filteredEvents.some(
            (e) =>
              (e.slug || e.event_slug) ===
              (preSelectedEvent.slug || preSelectedEvent.event_slug)
          )
        ) {
          setSelectedEvent(preSelectedEvent);
        } else if (filteredEvents.length > 0) {
          setSelectedEvent(filteredEvents[0]);
        }
      } else {
        toast.error(response?.message || "Failed to fetch trade missions");
        setEvents([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching trade missions"
      );
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (eventSlug) => {
    setAttendeesLoading(true);
    try {
      const response = await eventService.getEventAttendees(eventSlug);
      if (response?.status) {
        let attendeesData = [];
        if (response.data.status_code === 200) {
          // Handle the nested object structure from the API
          const data = response.data.data;
          if (typeof data === "object" && !Array.isArray(data)) {
            // Convert object with numeric keys to array
            attendeesData = Object.values(data);
          } else if (Array.isArray(data)) {
            attendeesData = data;
          }
        } else if (Array.isArray(response.data)) {
          attendeesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          attendeesData = response.data.data;
        }
        setAttendees(attendeesData);
      } else {
        toast.error(response?.message || "Failed to fetch attendees");
        setAttendees([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching attendees"
      );
      setAttendees([]);
    } finally {
      setAttendeesLoading(false);
    }
  };

  // Get payment status from attendee
  const getPaymentStatus = useCallback((attendee) => {
    return (
      attendee.payment_status ||
      attendee.payment?.status ||
      attendee.status ||
      attendee.registration_status ||
      "pending"
    );
  }, []);

  const getEventStatus = useCallback((attendeeOrEvent) => {
    if (attendeeOrEvent?.event) {
      return attendeeOrEvent.event.event_status || null;
    } else {
      return attendeeOrEvent?.event_status || null;
    }
  }, []);

  // Handle both DDMMYYYY (current API) and YYYYMMDD (legacy) DOB formats
  const formatDob = useCallback((dobValue) => {
    if (!dobValue) return "N/A";

    const formatDateParts = (day, month, year) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const dayText = String(day).padStart(2, "0");
      return `${dayText}-${monthNames[month - 1]}-${year}`;
    };

    const dobString = String(dobValue).trim();
    if (/^\d{8}$/.test(dobString)) {
      const tryBuildDate = (day, month, year) => {
        const parsedDate = new Date(year, month - 1, day);
        const isValidDate =
          parsedDate.getFullYear() === year &&
          parsedDate.getMonth() === month - 1 &&
          parsedDate.getDate() === day;
        return isValidDate ? formatDateParts(day, month, year) : null;
      };

      // Prefer current API format DDMMYYYY first
      const dmyFormatted = tryBuildDate(
        Number(dobString.slice(0, 2)),
        Number(dobString.slice(2, 4)),
        Number(dobString.slice(4, 8))
      );
      if (dmyFormatted) return dmyFormatted;

      // Fallback for legacy YYYYMMDD
      const ymdFormatted = tryBuildDate(
        Number(dobString.slice(6, 8)),
        Number(dobString.slice(4, 6)),
        Number(dobString.slice(0, 4))
      );
      if (ymdFormatted) {
        return ymdFormatted;
      }
    }

    const fallbackDate = new Date(dobString);
    if (Number.isNaN(fallbackDate.getTime())) return dobString;

    return formatDateParts(
      fallbackDate.getDate(),
      fallbackDate.getMonth() + 1,
      fallbackDate.getFullYear()
    );
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalAttendees = attendees.length;

    // Calculate revenue and payment statuses
    let totalRevenue = 0;
    let paidCount = 0;
    let pendingCount = 0;

    attendees.forEach((attendee) => {
      const paymentStatus = getPaymentStatus(attendee);

      // Get payment amount - look for various possible fields
      const amount =
        parseFloat(attendee.amount_paid || 0) ||
        parseFloat(attendee.payment?.amount || 0) ||
        parseFloat(attendee.total_amount || 0) ||
        parseFloat(attendee.price || 0) ||
        0;

      totalRevenue += amount;

      if (
        paymentStatus === "paid" ||
        paymentStatus === "completed" ||
        paymentStatus === "verified" ||
        paymentStatus === "approved"
      ) {
        paidCount++;
      } else {
        pendingCount++;
      }
    });

    return {
      totalAttendees,
      totalRevenue,
      paidCount,
      pendingCount,
    };
  }, [attendees, getPaymentStatus]);

  // Filter and search attendees
  const filteredAttendees = useMemo(() => {
    return attendees
      .filter((attendee) => {
        const user = attendee.user || {};
        const fullName = `${user.first_name || ""} ${
          user.last_name || ""
        }`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        const phone = (user.phone_number || "").toLowerCase();
        const location = (user.location || "").toLowerCase();

        const matchesSearch =
          fullName.includes(searchTerm.toLowerCase()) ||
          email.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase()) ||
          location.includes(searchTerm.toLowerCase());

        const matchesPaymentStatus =
          paymentStatusFilter === "all" ||
          getPaymentStatus(attendee) === paymentStatusFilter;

        const matchesVerification =
          verificationFilter === "all" ||
          (verificationFilter === "verified" &&
            (user.email_verified_at || user.phone_number_verified_at)) ||
          (verificationFilter === "unverified" &&
            !user.email_verified_at &&
            !user.phone_number_verified_at);

        return matchesSearch && matchesPaymentStatus && matchesVerification;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = `${a.user?.first_name || ""} ${
              a.user?.last_name || ""
            }`.toLowerCase();
            bValue = `${b.user?.first_name || ""} ${
              b.user?.last_name || ""
            }`.toLowerCase();
            break;
          case "email":
            aValue = (a.user?.email || "").toLowerCase();
            bValue = (b.user?.email || "").toLowerCase();
            break;
          case "location":
            aValue = (a.user?.location || "").toLowerCase();
            bValue = (b.user?.location || "").toLowerCase();
            break;
          case "verification":
            aValue =
              a.user?.email_verified_at || a.user?.phone_number_verified_at
                ? 1
                : 0;
            bValue =
              b.user?.email_verified_at || b.user?.phone_number_verified_at
                ? 1
                : 0;
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
  }, [
    attendees,
    searchTerm,
    paymentStatusFilter,
    verificationFilter,
    sortBy,
    sortOrder,
    getPaymentStatus,
  ]);

  // Pagination
  const paginatedAttendees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAttendees.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAttendees, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAttendees.length / itemsPerPage);

  // Handlers
  const handleViewAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setShowViewModal(true);
  };

  const handleBack = () => {
    navigate("/admin/media/trade-missions");
  };

  // Get verification status badge
  const getVerificationBadge = (user) => {
    if (user?.email_verified_at || user?.phone_number_verified_at) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 whitespace-nowrap bg-green-100 rounded-full">
          <CheckCircle2 className="mr-1 w-3 h-3" />
          Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 whitespace-nowrap bg-yellow-100 rounded-full">
        <AlertCircle className="mr-1 w-3 h-3" />
        Unverified
      </span>
    );
  };

  const getEventStatusBadge = (attendee) => {
    const eventStatus = getEventStatus(attendee);
    if (eventStatus === "approved") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 whitespace-nowrap bg-green-100 rounded-full">
          <CheckCircle2 className="mr-1 w-3 h-3" />
          Approved
        </span>
      );
    } else if (eventStatus === "rejected") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 whitespace-nowrap bg-red-100 rounded-full">
          <XCircle className="mr-1 w-3 h-3" />
          Rejected
        </span>
      );
    } else if (eventStatus === "pending") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 whitespace-nowrap bg-yellow-100 rounded-full">
          <Timer className="mr-1 w-3 h-3" />
          Pending Review
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 whitespace-nowrap bg-gray-100 rounded-full">
          <Timer className="mr-1 w-3 h-3" />
          Unknown
        </span>
      );
    }
  };

  // Get payment status badge
  const getPaymentStatusBadge = (attendee) => {
    const status = getPaymentStatus(attendee);
    const statusLower = status.toLowerCase();

    if (
      statusLower === "paid" ||
      statusLower === "completed" ||
      statusLower === "verified" ||
      statusLower === "approved"
    ) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 whitespace-nowrap bg-green-100 rounded-full">
          <CheckCircle2 className="mr-1 w-3 h-3" />
          Paid
        </span>
      );
    } else if (statusLower === "rejected" || statusLower === "declined") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 whitespace-nowrap bg-red-100 rounded-full">
          <XCircle className="mr-1 w-3 h-3" />
          Rejected
        </span>
      );
    } else if (statusLower === "approved") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 whitespace-nowrap bg-blue-100 rounded-full">
          <CheckCircle className="mr-1 w-3 h-3" />
          Approved
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 whitespace-nowrap bg-yellow-100 rounded-full">
          <Timer className="mr-1 w-3 h-3" />
          Pending Payment
        </span>
      );
    }
  };

  // Action handlers - open modals
  const handleApprove = (attendee) => {
    setAttendeeToAction(attendee);
    setShowApproveModal(true);
  };

  const handleMarkAsPaid = (attendee) => {
    setAttendeeToAction(attendee);
    setShowMarkAsPaidModal(true);
  };

  const handleViewRejectionReason = (attendee) => {
    setAttendeeToAction(attendee);
    setShowRejectionReasonModal(true);
  };

  // Confirm handlers
  const handleApproveConfirm = async () => {
    if (!attendeeToAction) return;

    setIsProcessing(true);
    try {
      // TODO: Replace with actual API call
      const response = await eventService.approveAttendeeRegistration(
        attendeeToAction.event_slug
      );
      if (response?.data?.status_code === 200) {
        toast.success(
          response?.data?.reason || "Registration approved successfully"
        );
        fetchAttendees(attendeeToAction.event_slug);
      } else {
        toast.error(response?.data?.reason || "Failed to approve registration");
      }
    } catch (error) {
      toast.error(error.message || "Failed to approve registration");
    } finally {
      setShowApproveModal(false);
      setAttendeeToAction(null);
      setIsProcessing(false);
    }
  };

  const handleMarkAsPaidConfirm = async () => {
    if (!attendeeToAction) return;

    setIsProcessing(true);
    try {
      const slug = attendeeToAction?.slug;
      if (!slug) {
        toast.error("No Attendee data specified");
        return;
      }
      const response = await eventService.markAttendeeAsPaid(slug);
      if (response?.data?.status_code === 200) {
        toast.success(
          response?.data?.reason || "Registration marked as paid successfully"
        );
        // Refresh attendees list to show updated data
        if (selectedEvent) {
          fetchAttendees(selectedEvent.slug || selectedEvent.event_slug);
        }
        setShowMarkAsPaidModal(false);
        setAttendeeToAction(null);
      } else {
        toast.error(response?.data?.reason || "Failed to mark as paid");
        setShowMarkAsPaidModal(false);
        setAttendeeToAction(null);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          error.message ||
          "Failed to mark as paid"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader fullScreen={true} text="Loading trade missions..." />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-4 space-y-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
              <Briefcase className="flex-shrink-0 mr-2 w-6 h-6 text-orange-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
              <span className="truncate">Trade Mission Attendees</span>
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
              Manage attendees for trade missions
            </p>
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => {
                fetchEvents();
                if (selectedEvent) {
                  fetchAttendees(
                    selectedEvent.slug || selectedEvent.event_slug
                  );
                }
              }}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="flex-shrink-0 mr-2 w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleBack}
              className="bg-white text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <ArrowLeft className="flex-shrink-0 mr-2 w-4 h-4" />
              <span>Back</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-2 sm:gap-4 lg:gap-6">
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Total Attendees
                </p>
                <p className="mt-1 text-xl font-bold text-orange-600 sm:text-2xl lg:text-3xl">
                  {stats.totalAttendees}
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-orange-100 rounded-lg sm:p-3">
                <Users className="w-4 h-4 text-orange-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Paid / Pending
                </p>
                <p className="mt-1 text-xl font-bold text-purple-600 sm:text-2xl lg:text-3xl">
                  {stats.paidCount} / {stats.pendingCount}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {stats.paidCount} paid, {stats.pendingCount} pending
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-purple-100 rounded-lg sm:p-3">
                <CreditCard className="w-4 h-4 text-purple-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Event Selector & Filters */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl sm:p-6">
          {/* Event Selector */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Select Trade Mission
            </label>
            <select
              value={selectedEvent?.slug || selectedEvent?.event_slug || ""}
              onChange={(e) => {
                const event = events.find(
                  (ev) => (ev.slug || ev.event_slug) === e.target.value
                );
                setSelectedEvent(event);
                setCurrentPage(1);
              }}
              className="px-4 py-3 w-full text-sm bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {events.map((event) => (
                <option
                  key={event.slug || event.event_slug}
                  value={event.slug || event.event_slug}
                >
                  {event.event_title || event.venue_name}
                </option>
              ))}
            </select>
          </div>

          {/* Mobile Filter Toggle */}
          <div className="flex justify-between items-center mb-4 lg:hidden">
            <h3 className="flex items-center text-base font-semibold text-gray-900">
              <Filter className="mr-2 w-4 h-4 text-orange-600" />
              Filters
            </h3>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 touch-manipulation"
            >
              <span>Filters</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${
                  showMobileFilters ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* Desktop Filter Header */}
          <div className="hidden justify-between items-center mb-6 lg:flex">
            <h3 className="flex items-center text-lg font-semibold text-gray-900">
              <Filter className="mr-2 w-5 h-5 text-orange-600" />
              Filters & Search
            </h3>
          </div>

          {/* Filter Controls */}
          <div
            className={`space-y-4 ${
              showMobileFilters ? "block" : "hidden lg:block"
            }`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute top-3 left-3 w-4 h-4 text-gray-400 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search by name, email, phone, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-3 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 transition-all sm:text-base focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {/* Payment Status Filter */}
              <select
                value={paymentStatusFilter}
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 sm:px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Payment Status</option>
                <option value="paid">Paid Only</option>
                <option value="pending">Pending Only</option>
                <option value="approved">Approved Only</option>
                <option value="rejected">Rejected Only</option>
              </select>

              {/* Verification Filter */}
              <select
                value={verificationFilter}
                onChange={(e) => {
                  setVerificationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 sm:px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Verification</option>
                <option value="verified">Verified Only</option>
                <option value="unverified">Unverified Only</option>
              </select>

              {/* Sort Order */}
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="flex justify-center items-center px-3 py-3 text-sm bg-gray-100 rounded-lg border border-gray-300 transition-colors sm:px-4 hover:bg-gray-200 active:bg-gray-300 touch-manipulation"
              >
                {sortOrder === "asc" ? "↑" : "↓"}{" "}
                {sortOrder === "asc" ? "Ascending" : "Descending"}
              </button>
            </div>

            {/* Sort By */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <button
                onClick={() => setSortBy("name")}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === "name"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Sort by Name
              </button>
              <button
                onClick={() => setSortBy("email")}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === "email"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Sort by Email
              </button>
              <button
                onClick={() => setSortBy("location")}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === "location"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Sort by Location
              </button>
              <button
                onClick={() => setSortBy("verification")}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === "verification"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Sort by Verification
              </button>
            </div>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm ||
          paymentStatusFilter !== "all" ||
          verificationFilter !== "all") && (
          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                setSearchTerm("");
                setPaymentStatusFilter("all");
                setVerificationFilter("all");
              }}
              className="px-3 py-2 text-sm text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Attendees Display */}
        {attendeesLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader text="Loading attendees..." />
          </div>
        ) : (
          <>
            {/* Attendees Table/Cards */}
            <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl">
              {/* Table Header */}
              <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 sm:px-4 lg:px-6 sm:py-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                    Attendee List ({filteredAttendees.length}{" "}
                    {filteredAttendees.length === 1 ? "attendee" : "attendees"})
                  </h3>
                  <div className="hidden text-xs text-gray-500 sm:block sm:text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Attendee Details
                      </th>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Contact Info
                      </th>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Location
                      </th>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Verification
                      </th>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                        Event Status
                      </th>

                      <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedAttendees.map((attendee, index) => {
                      const user = attendee.user || {};
                      const event = attendee?.event || {};
                      return (
                        <tr
                          key={attendee.user_slug || index}
                          className="transition-colors duration-150 hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full shadow-sm">
                                <span className="text-sm font-medium text-white">
                                  {user.first_name?.charAt(0) || ""}
                                  {user.last_name?.charAt(0) || ""}
                                </span>
                              </div>
                              <div className="ml-4 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {user.first_name} {user.last_name}
                                </div>
                                <div className="flex items-center text-sm text-gray-500 truncate">
                                  {user.gender && (
                                    <>
                                      <span className="flex-shrink-0 mr-2 w-2 h-2 bg-gray-300 rounded-full"></span>
                                      {user.gender.charAt(0).toUpperCase() +
                                        user.gender.slice(1)}
                                    </>
                                  )}
                                  {user.dob && (
                                    <span className="ml-2 text-gray-400 truncate">
                                      • DOB: {formatDob(user.dob)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-900 truncate">
                                <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                                <span className="truncate">
                                  {user.email || "N/A"}
                                </span>
                              </div>
                              <div className="flex items-center text-sm text-gray-500 truncate">
                                <Phone className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                                <span className="truncate">
                                  {user.phone_number || "N/A"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center min-w-0">
                              <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {user.location || "N/A"}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {user.region || "N/A"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            {getVerificationBadge(user)}
                            <div className="mt-1 text-xs text-gray-500 whitespace-nowrap">
                              Email {user.email_verified_at ? "✓" : "✗"} | Phone{" "}
                              {user.phone_number_verified_at ? "✓" : "✗"}
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            {getPaymentStatusBadge(attendee)}
                          </td>
                          <td className="px-6 py-4 align-top">
                            {getEventStatusBadge(event)}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex gap-2 justify-end items-center">
                              <button
                                onClick={() => handleViewAttendee(attendee)}
                                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-orange-600 hover:bg-orange-50"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <AttendeeActionsMenu
                                onView={() => handleViewAttendee(attendee)}
                                onApprove={() => handleApprove(attendee)}
                                onMarkAsPaid={() => handleMarkAsPaid(attendee)}
                                onViewRejectionReason={() =>
                                  handleViewRejectionReason(attendee)
                                }
                                isProcessing={isProcessing}
                                paymentStatus={getPaymentStatus(attendee)}
                                eventStatus={getEventStatus(attendee.event)}
                                align="right"
                                hasRejectionReason={
                                  !!attendee?.event?.event_reject_reason &&
                                  attendee?.event?.event_status === "rejected"
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="divide-y divide-gray-200 lg:hidden">
                {paginatedAttendees.map((attendee, index) => {
                  const user = attendee.user || {};
                  return (
                    <div
                      key={attendee.user_slug || index}
                      className="p-4 transition-colors hover:bg-gray-50"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex flex-1 items-center min-w-0">
                          <div className="flex flex-shrink-0 justify-center items-center mr-3 w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full shadow-sm">
                            <span className="text-sm font-medium text-white">
                              {user.first_name?.charAt(0) || ""}
                              {user.last_name?.charAt(0) || ""}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-semibold text-gray-900 truncate">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="flex flex-wrap gap-2 items-center mt-1">
                              {getVerificationBadge(user)}
                              {getPaymentStatusBadge(attendee)}
                              {getEventStatusBadge(attendee)}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => handleViewAttendee(attendee)}
                            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-orange-600 hover:bg-orange-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <AttendeeActionsMenu
                            onView={() => handleViewAttendee(attendee)}
                            onApprove={() => handleApprove(attendee)}
                            onMarkAsPaid={() => handleMarkAsPaid(attendee)}
                            onViewRejectionReason={() =>
                              handleViewRejectionReason(attendee)
                            }
                            isProcessing={isProcessing}
                            paymentStatus={getPaymentStatus(attendee)}
                            eventStatus={getEventStatus(attendee.event)}
                            align="right"
                            hasRejectionReason={
                              !!attendee?.event?.event_reject_reason &&
                              attendee?.event?.event_status === "rejected"
                            }
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {/* Contact Info */}
                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 truncate">
                              {user.email || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center text-sm">
                            <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 truncate">
                              {user.phone_number || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Location */}
                        <div className="flex items-center text-sm">
                          <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                          <span className="text-gray-900 truncate">
                            {user.location || "N/A"}, {user.region || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Empty State */}
              {filteredAttendees.length === 0 && !attendeesLoading && (
                <div className="py-12 text-center lg:py-16">
                  <Users className="mx-auto w-12 h-12 text-gray-300 lg:h-16 lg:w-16" />
                  <h3 className="mt-4 text-base font-medium text-gray-900 lg:text-lg">
                    No attendees found
                  </h3>
                  <p className="px-4 mx-auto mt-2 max-w-sm text-sm text-gray-500 lg:text-sm">
                    {searchTerm ||
                    paymentStatusFilter !== "all" ||
                    verificationFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : selectedEvent
                      ? "No attendees registered for this trade mission yet."
                      : "Please select a trade mission to view attendees."}
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredAttendees.length > 0 && (
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl sm:p-4">
                <div className="flex flex-col justify-between items-center space-y-2 sm:flex-row sm:space-y-0">
                  <div className="text-xs text-center text-gray-500 sm:text-sm sm:text-left">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredAttendees.length
                    )}{" "}
                    of {filteredAttendees.length} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-xs text-gray-700 bg-gray-100 rounded-lg transition-colors sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                    >
                      Previous
                    </button>
                    <div className="flex items-center px-3 py-2 text-xs font-medium text-white bg-orange-600 rounded-lg sm:text-sm">
                      {currentPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-xs text-gray-700 bg-gray-100 rounded-lg transition-colors sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* View Attendee Modal */}
        <AnimatePresence>
          {showViewModal && selectedAttendee && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center p-4"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              onClick={() => setShowViewModal(false)}
            >
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              />

              {/* Modal */}
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              >
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-full backdrop-blur">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          Attendee Details
                        </h3>
                        <p className="text-sm text-orange-100">
                          {selectedEvent?.event_title || "Trade Mission"}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="p-2 text-white rounded-lg transition-colors hover:text-orange-200 hover:bg-white hover:bg-opacity-20"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 p-6">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Personal Information */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <User className="mr-2 w-4 h-4 text-orange-600" />
                        Personal Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Full Name
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {selectedAttendee.user?.first_name}{" "}
                            {selectedAttendee.user?.last_name}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Gender
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900 capitalize">
                            {selectedAttendee.user?.gender || "N/A"}
                          </div>
                        </div>
                        {selectedAttendee.user?.dob && (
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Date of Birth
                            </label>
                            <div className="mt-1 text-sm font-medium text-gray-900">
                              {formatDob(selectedAttendee.user.dob)}
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            User Slug
                          </label>
                          <div className="px-2 py-1 mt-1 font-mono text-xs bg-gray-100 rounded">
                            {selectedAttendee.user_slug}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <Mail className="mr-2 w-4 h-4 text-orange-600" />
                        Contact Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Email Address
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900 break-all">
                            {selectedAttendee.user?.email || "N/A"}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Phone Number
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {selectedAttendee.user?.phone_number || "N/A"}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Channel
                          </label>
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-800 capitalize bg-orange-100 rounded-md">
                              {selectedAttendee.user?.channel || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <MapPin className="mr-2 w-4 h-4 text-orange-600" />
                        Location Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Location
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {selectedAttendee.user?.location || "N/A"}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Region
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {selectedAttendee.user?.region || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Verification Status */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <CheckCircle2 className="mr-2 w-4 h-4 text-green-600" />
                        Verification Status
                      </h4>
                      <div className="space-y-2">
                        <div
                          className={`flex items-center px-3 py-2 rounded-lg ${
                            selectedAttendee.user?.email_verified_at
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedAttendee.user?.email_verified_at
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <span className="text-sm font-medium">
                            {selectedAttendee.user?.email_verified_at
                              ? "Email Verified"
                              : "Email Not Verified"}
                          </span>
                        </div>
                        <div
                          className={`flex items-center px-3 py-2 rounded-lg ${
                            selectedAttendee.user?.phone_number_verified_at
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedAttendee.user?.phone_number_verified_at
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <span className="text-sm font-medium">
                            {selectedAttendee.user?.phone_number_verified_at
                              ? "Phone Verified"
                              : "Phone Not Verified"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Status */}
                    {(() => {
                      const paymentStatus = getPaymentStatus(selectedAttendee);
                      const isPaid = paymentStatus === "paid" || paymentStatus === "completed" || paymentStatus === "verified";
                      return (
                        <div className={`p-4 bg-gradient-to-r rounded-xl border lg:col-span-2 ${
                          isPaid 
                            ? "from-green-50 to-green-50 border-green-200" 
                            : "from-orange-50 to-orange-50 border-orange-200"
                        }`}>
                          <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                            <CreditCard className={`mr-2 w-4 h-4 ${
                              isPaid ? "text-green-600" : "text-orange-600"
                            }`} />
                            Payment Status
                          </h4>
                          <div className="flex justify-between items-center">
                            <div>
                              {getPaymentStatusBadge(selectedAttendee)}
                              <p className="mt-2 text-sm text-gray-600">
                                {paymentStatus === "paid"
                                  ? "Payment has been confirmed."
                                  : paymentStatus === "approved"
                                  ? "Registration has been approved."
                                  : paymentStatus === "rejected"
                                  ? "Registration has been rejected."
                                  : "Payment is pending confirmation."}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Document Information (if available) */}
                    {selectedAttendee.user?.document_type && (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 lg:col-span-2">
                        <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                          <Building2 className="mr-2 w-4 h-4 text-orange-600" />
                          Document Information
                        </h4>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Document Type
                            </label>
                            <p className="mt-1 text-sm font-medium text-gray-900 capitalize">
                              {selectedAttendee.user.document_type?.replace(
                                /_/g,
                                " "
                              )}
                            </p>
                          </div>
                          {selectedAttendee.user.card_number && (
                            <div>
                              <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                Card Number
                              </label>
                              <p className="mt-1 font-mono text-sm text-gray-900">
                                {selectedAttendee.user.card_number}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedAttendee(null);
                    }}
                    className="px-6 py-2 font-medium text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Approve Attendee Modal */}
        <AnimatePresence>
          {showApproveModal && attendeeToAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center p-4"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <div
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
                onClick={() => {
                  if (!isProcessing) {
                    setShowApproveModal(false);
                    setAttendeeToAction(null);
                  }
                }}
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all transform"
              >
                {/* Header */}
                <div className="flex items-start mb-6 space-x-4">
                  <div className="flex-shrink-0 p-3 bg-blue-100 rounded-full">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="mb-1 text-xl font-bold text-gray-900">
                      Approve Registration
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to approve the registration for{" "}
                      <span className="font-semibold text-gray-900">
                        {attendeeToAction.user?.first_name}{" "}
                        {attendeeToAction.user?.last_name}
                      </span>
                      ?
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!isProcessing) {
                        setShowApproveModal(false);
                        setAttendeeToAction(null);
                      }
                    }}
                    disabled={isProcessing}
                    className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Info Message */}
                <div className="p-4 mb-6 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="mb-1 font-medium">Note:</p>
                      <p>
                        This will mark the registration as approved. The
                        attendee will be notified.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isProcessing) {
                        setShowApproveModal(false);
                        setAttendeeToAction(null);
                      }
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApproveConfirm}
                    disabled={isProcessing}
                    className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-blue-600 rounded-xl transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      "Approve Registration"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mark as Paid Modal */}
        <AnimatePresence>
          {showMarkAsPaidModal && attendeeToAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center p-4"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <div
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
                onClick={() => {
                  if (!isProcessing) {
                    setShowMarkAsPaidModal(false);
                    setAttendeeToAction(null);
                  }
                }}
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all transform"
              >
                {/* Header */}
                <div className="flex items-start mb-6 space-x-4">
                  <div className="flex-shrink-0 p-3 bg-green-100 rounded-full">
                    <CreditCard className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="mb-1 text-xl font-bold text-gray-900">
                      Mark as Paid
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to mark the registration as paid for{" "}
                      <span className="font-semibold text-gray-900">
                        {attendeeToAction.user?.first_name}{" "}
                        {attendeeToAction.user?.last_name}
                      </span>
                      ?
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!isProcessing) {
                        setShowMarkAsPaidModal(false);
                        setAttendeeToAction(null);
                      }
                    }}
                    disabled={isProcessing}
                    className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Info Message */}
                <div className="p-4 mb-6 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="mb-1 font-medium">Note:</p>
                      <p>
                        This will mark the payment as confirmed. The attendee's
                        registration status will be updated to paid.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isProcessing) {
                        setShowMarkAsPaidModal(false);
                        setAttendeeToAction(null);
                      }
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleMarkAsPaidConfirm}
                    disabled={isProcessing}
                    className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-green-600 rounded-xl transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Mark as Paid"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Rejection Reason Modal */}
        <AnimatePresence>
          {showRejectionReasonModal && attendeeToAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center p-4"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <div
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
                onClick={() => {
                  setShowRejectionReasonModal(false);
                  setAttendeeToAction(null);
                }}
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all transform"
              >
                {/* Header */}
                <div className="flex items-start mb-6 space-x-4">
                  <div className="flex-shrink-0 p-3 bg-orange-100 rounded-full">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="mb-1 text-xl font-bold text-gray-900">
                      Rejection Reason
                    </h3>
                    <p className="text-sm text-gray-600">
                      Registration rejection reason for{" "}
                      <span className="font-semibold text-gray-900">
                        {attendeeToAction.user?.first_name}{" "}
                        {attendeeToAction.user?.last_name}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowRejectionReasonModal(false);
                      setAttendeeToAction(null);
                    }}
                    className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Rejection Reason Content */}
                <div className="mb-6">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm text-red-800">
                        <p className="mb-2 font-medium">Rejection Reason:</p>
                        <p className="whitespace-pre-wrap break-words">
                          {attendeeToAction?.event?.event_reject_reason ||
                            "No reason provided"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRejectionReasonModal(false);
                      setAttendeeToAction(null);
                    }}
                    className="flex-1 px-4 py-3 font-semibold text-white bg-orange-600 rounded-xl transition-colors hover:bg-orange-700"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TradeMissionAttendees;
